/**
 * Step 2 — "Payment Details" + the POST — [BUT-843, ours]. BUILT.
 *
 * Mirrors `PaymentDetailsStep`'s payment block verbatim (reuse, not
 * re-implementation). Whether a card is needed, and how much it will be
 * charged, is the SERVER's answer (`useLessonBookingQuote`): a lesson bills the
 * instructor's rate for whatever the member's package does not cover, plus
 * tax, and none of that can be worked out here. Until the quote answers
 * nothing may be booked — an unanswered quote must not read as a covered lesson.
 *
 * Double-tap is stopped by a synchronous `useRef` latch — `isPending` alone
 * is insufficient because two clicks in one frame both read `false` before
 * the re-render. The latch covers the work this step owns: it is set at the
 * click for `confirm`, and when a new card comes back saved, and cleared only
 * on a terminal failure, never on success. The card form's own submit is not
 * latched, so a declined card can be tried again. The request body is exactly
 * `{ bayId, instructorId, startTime, endTime, paymentMethodId? }` — never
 * `userId` (it comes from the token). `startTime`/`endTime` are
 * converted from club wall-clock through `createClubDateTimeFromSlot`,
 * never `createClubDateTime` (slot-times.md — `endTime` may be the "24:00"
 * sentinel).
 *
 * The sign-in gate ("Sign in to book" swapping this CTA for a guest) is
 * Step 14's page-shell concern, not this step's — `resolveLessonCta` takes
 * no `isAuthenticated` input, and this step is only reachable once a member
 * is signed in.
 *
 * @module pages/lessons/steps/LessonPaymentStep
 */

import { useEffect, useMemo, useRef, useState, type MouseEvent, type ReactNode } from 'react';
import { format, parseISO } from 'date-fns';
import { formatFeeRate } from '@butter/library/pricing';
import { TicketCard, TicketDivider } from '@butter/ui/patterns';
import { createClubDateTimeFromSlot, formatTimeRange } from '@butter/library/datetime';
import { ArrowIcon, CheckmarkIcon } from '@butter/ui/icons';
import { PaymentMethodSelector, UNSELECTABLE_METHOD_TYPES } from '@butter/ui/domains/billing';
import {
  usePaymentMethodsQuery,
  useCardSave,
  CardEntryForm,
  reportPaymentClientError,
} from '~/features/payment';
import { useAuth } from '~/services/auth';
import { useClubQuery } from '~/services/internalApi/club/hooks/useClubQuery';
import { useHomeClub } from '~/services/internalApi/club';
import { useTenantHandle } from '~/services/appRuntime';
import { useTenantNavigate } from '~/lib/navigation';
import { detectPaymentProvider, getPaymentSettings } from '~/lib/utils/paymentProvider';
import { safeToNumericUserId } from '~/lib/utils/userIdUtils';
import {
  useLessonFlow,
  useCreateLessonBooking,
  useLessonBookingQuote,
  formatLessonHours,
} from '~/services/internalApi/lessons';
import type { CreateLessonBookingRequest } from '~/services/internalApi/lessons';
import { clearLessonDisplayExtras } from '~/services/internalApi/lessons/context/sessionStorage';
import {
  isOverLessonDurationLimit,
  LESSON_PACK_FALLBACK_NAME,
  lessonCoverageSentence,
  lessonHoursUsedSentence,
  lessonPartialCoverageSentence,
} from '@butter/library/lessons';
import {
  toLessonBookingFailure,
  GENERIC_LESSON_FAILURE,
  LESSON_DURATION_LIMIT_FAILURE,
} from '../lessonBookingErrors';
import type { LessonBookingFailure, LessonBookingRecovery } from '../lessonBookingErrors';
import { resolveLessonCta } from '../lessonCheckoutCta';
import { resolveLessonBayName } from '../lessonBayName';
import { useLessonInstructor } from '../useLessonInstructor';
import { LessonDetailsCard, LessonCoverageCard, LessonTicketRow } from '../components';
import { RetryBanner } from '@butter/ui/components/feedback';
import { QuoteRefusedPanel } from '~/pages/subscription/steps/QuoteBanners';
import { LessonFailureNotice } from './components';
import { CheckoutSection } from '~/pages/booking/components';
import { WaiverSection } from '~/pages/booking/components/WaiverSection';
import { useWaiverSubmission } from '~/pages/booking/components/useWaiverSubmission';
import { waiverRefusalMessage } from '~/pages/booking/components/waiverCompletion';

const LESSON_CARD_FORM_ID = 'lesson-card-form';

const CTA_CLASSES =
  'w-full rounded-full h-12 md:h-16 px-6 text-base md:text-xl font-semibold bg-theme-600 text-white text-interactive:bg-theme-700 disabled:bg-butter-gray-100 disabled:text-fg-muted disabled:cursor-not-allowed transition-colors';

/** A guard/waiver-refusal throw becomes a failure with the thrown message, not a generic one. */
function guardFailure(err: unknown): LessonBookingFailure {
  return {
    ...GENERIC_LESSON_FAILURE,
    message: err instanceof Error ? err.message : GENERIC_LESSON_FAILURE.message,
  };
}

export function LessonPaymentStep() {
  const { data: club } = useClubQuery();
  const { homeClub } = useHomeClub();
  const { user: authUser, isAuthenticated } = useAuth();
  const [tenantHandle] = useTenantHandle();
  const navigateWithParams = useTenantNavigate();

  const userId = safeToNumericUserId(authUser?.id);

  const {
    instructorId,
    bayId,
    date,
    startTime,
    endTime,
    durationMinutes,
    instructorName,
    instructorImage,
    goToStep,
  } = useLessonFlow();

  const selectionComplete =
    instructorId !== null && bayId !== null && date !== null && startTime !== null && endTime !== null;

  // Missing required params (a direct URL visit that skipped earlier steps) —
  // mirrors `ConfirmBookingPage`'s redirect-when-no-booking-info guard.
  useEffect(() => {
    if (!selectionComplete) goToStep(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectionComplete]);

  // Instructor resolution (QA MUST-FIX): pack whitelist → club roster →
  // session sugar (`useLessonFlow`'s own staleness guard already applies) →
  // placeholder. The club query mounts here only for a no-pack member,
  // sharing step 0's query key — usually a cache hit, not a new network call.
  const { userLessons, instructor: resolvedInstructor } = useLessonInstructor({
    instructorId,
    clubId: club?.id,
    isAuthenticated,
    sessionName: instructorName,
    sessionImage: instructorImage,
  });
  const displayInstructorName = resolvedInstructor?.name ?? 'Your instructor';
  const displayInstructorImage = resolvedInstructor?.image ?? null;
  const instructorUnresolved = resolvedInstructor === null;

  const overLimit = isOverLessonDurationLimit(durationMinutes ?? 0);

  // The slot as the two instants the server takes. Strings, so they are stable
  // in the query key. `createClubDateTimeFromSlot`, never `createClubDateTime`:
  // an end may be the "24:00" sentinel.
  const canResolveSlot = !!club?.timezone && date !== null && startTime !== null && endTime !== null;
  const startTimeUtc = canResolveSlot
    ? createClubDateTimeFromSlot(date, startTime, club.timezone).date.toISOString()
    : null;
  const endTimeUtc = canResolveSlot
    ? createClubDateTimeFromSlot(date, endTime, club.timezone).date.toISOString()
    : null;

  // ── Server quote — the only source of money and of the card decision ──
  const quoteQuery = useLessonBookingQuote({
    clubId: club?.id,
    instructorId,
    bayId,
    startTime: startTimeUtc,
    endTime: endTimeUtc,
    // An over-long lesson is refused before it is priced; the notice says why.
    enabled: isAuthenticated && !overLimit,
  });
  const quote = quoteQuery.quote;
  // The server answered, and the answer is that this slot cannot be booked.
  const quoteRefused = quote?.isValid === false;
  // No answer, and none coming without help. A failed BACKGROUND refetch is
  // not this: the last price is still a real answer.
  const quoteFailed =
    !!quoteQuery.failure || (!quoteQuery.hasAnswer && (quoteQuery.isError || quoteQuery.isPaused));
  const priced = quote?.isValid === true;
  const coverage = priced ? (quote.coverage ?? null) : null;
  const totalCost = priced ? quote.totalCost : undefined;
  // Owed only when the server priced something. An absent `totalCost` on a
  // valid quote is "priced at nothing" — the package covers the whole session.
  const owed = totalCost !== undefined;
  // Owed, but the member's account credit pays all of it (credits X6): no card. The
  // server's verdict, never a client comparison — an absent verdict means a card.
  const creditCovered = owed && quote?.isValid === true && quote.paymentMethodRequired === false;
  const needsCard = owed && !creditCovered;
  // The member's credit below the total (credits D9), when the quote applies any.
  const credit = priced && quote.credit && quote.credit.appliedCents > 0 ? quote.credit : null;
  // The pack the quote says will be drawn on, for its name and balance.
  const coveragePack =
    coverage?.userLessonId != null
      ? (userLessons.find((pack) => pack.id === coverage.userLessonId) ?? null)
      : null;

  const waiverGate = useWaiverSubmission(club?.id);

  // ── Payment state — the only `useState` on this step. Everything else is
  // derived in render from the queries + the flow context. ──────────────────
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<string | null>(null);
  const [showNewPaymentForm, setShowNewPaymentForm] = useState(false);
  const [cardReady, setCardReady] = useState(false);
  const [cardSubmitting, setCardSubmitting] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [failure, setFailure] = useState<LessonBookingFailure | null>(null);

  const mutation = useCreateLessonBooking();
  const busy = cardSubmitting || isProcessing || mutation.isPending;

  const { paymentMethods, isLoading: isLoadingPaymentMethods } = usePaymentMethodsQuery({
    // Never fetch or pre-select a card for a session that turns out to be
    // fully covered.
    enabled: isAuthenticated && needsCard,
  });

  // Payment methods are always created/charged at the user's home club, even
  // when visiting another club's portal — same rule `PaymentDetailsStep`
  // follows for an authenticated member.
  const effectivePaymentSettings = useMemo(() => getPaymentSettings(homeClub ?? club), [homeClub, club]);
  const paymentProvider = useMemo(
    () => detectPaymentProvider(effectivePaymentSettings),
    [effectivePaymentSettings],
  );

  // The new-card form is shown whenever we're NOT showing the saved-methods
  // list. A selection counts as "has saved methods" too — PaymentDetailsStep's
  // own derivation, verbatim.
  const showingNewPaymentForm = !(
    isAuthenticated &&
    !showNewPaymentForm &&
    (paymentMethods.length > 0 || selectedPaymentMethodId != null)
  );

  // Derived in render, not synced via effect — the first CHARGEABLE method
  // (default first), once everything has settled AND a card is actually
  // owed. Explicit user selection (setSelectedPaymentMethodId) always wins.
  const defaultSelectablePaymentMethodId = useMemo(() => {
    const selectable = paymentMethods.filter(
      (pm) => !UNSELECTABLE_METHOD_TYPES.includes(pm.methodType ?? 'card'),
    );
    const defaultMethod = selectable.find((pm) => pm.isDefault);
    return (defaultMethod ?? selectable[0])?.id.toString() ?? null;
  }, [paymentMethods]);

  const effectiveSelectedPaymentMethodId =
    isAuthenticated && needsCard && !showNewPaymentForm
      ? (selectedPaymentMethodId ?? defaultSelectablePaymentMethodId)
      : selectedPaymentMethodId;

  const { saveCard, ...cardSaveWiring } = useCardSave({
    userId: userId ?? undefined,
    clubId: club?.id,
    tenantHandle: tenantHandle || undefined,
    paymentProvider,
    stripeSetupEnabled: needsCard && paymentProvider === 'stripe' && showingNewPaymentForm,
  });

  const cta = resolveLessonCta({
    isLoadingPaymentMethods,
    priced,
    // What the card is charged: the total, or what is left after account credit.
    totalDue: needsCard ? (credit ? credit.dueNowCents / 100 : (totalCost?.total ?? null)) : null,
    needsCard,
    hasSelectedMethod: effectiveSelectedPaymentMethodId != null,
    showingNewPaymentForm,
    cardReady,
    waiverSatisfied: waiverGate.satisfied,
    overLimit,
    selectionComplete,
    busy,
  });

  // Synchronous latch around the work THIS step owns: saving the card to the
  // member and posting the booking. `busy` above is the visual half only —
  // two clicks in the same frame both read the same `isPending`/`isProcessing`
  // snapshot, so only a synchronous ref can stop the second one. Cleared only
  // on a terminal failure, never on success.
  //
  // It is NOT held across the card form's own submit. A decline or a failed
  // tokenization is reported inline by the form and never reaches this step,
  // so a latch taken at that click would have nothing to release it and the
  // member could not try the card again. The form guards its own in-flight
  // window, and neither provider's "stopped submitting" signal can stand in
  // for a release: both also fire it on unmount, which happens mid-booking
  // when a saved card hides the form.
  const submitLatch = useRef(false);

  /** Signs the waiver (if outstanding) and POSTs the booking. Throws on refusal. */
  async function submitBooking(paymentMethodId?: string) {
    if (!startTimeUtc || !endTimeUtc || instructorId === null || bayId === null) return;

    if (!waiverGate.satisfied) {
      throw new Error(waiverRefusalMessage(waiverGate.answerMissing));
    }
    // Sign BEFORE the booking is created — the alternative (book, then sign)
    // leaves a booking against an unsigned waiver on the second call's
    // failure.
    await waiverGate.submitWaivers();

    const body: CreateLessonBookingRequest = {
      bayId: Number(bayId),
      instructorId: Number(instructorId),
      // The same two instants the quote priced.
      startTime: startTimeUtc,
      endTime: endTimeUtc,
      ...(needsCard && paymentMethodId ? { paymentMethodId: Number(paymentMethodId) } : {}),
    };

    const res = await mutation.mutateAsync(body);
    if (res.success) {
      clearLessonDisplayExtras();
      navigateWithParams(`/lessons/confirmed/${res.payload.booking.id}`, {
        state: {
          booking: res.payload.booking,
          accessControl: res.payload.accessControl,
          access: res.payload.access,
          // The resolved name (pack/club/session), not the raw session-only
          // value — carries the real answer through to the confirmation page
          // even when the session sugar itself was the one that missed.
          instructorName: resolvedInstructor?.name,
        },
      });
      return; // latch stays set — we are leaving the page
    }
    setFailure(toLessonBookingFailure(res));
    // Credit no longer covers it (spent since the quote): refresh the price so the card
    // step returns (credits D30).
    if (res.error?.code === 'credit_balance_changed') void quoteQuery.refetch();
    submitLatch.current = false; // released ONLY on a terminal failure
  }

  /** `cta.action === 'confirm'` — no card to submit, or a saved method is already selected. */
  async function handleConfirm() {
    setIsProcessing(true);
    try {
      await submitBooking(needsCard ? (effectiveSelectedPaymentMethodId ?? undefined) : undefined);
    } catch (err) {
      setFailure(guardFailure(err));
      submitLatch.current = false;
      reportPaymentClientError('confirm_lesson_guards', err, {
        kind: 'client_flow',
        surface: 'web_lesson',
      });
    } finally {
      setIsProcessing(false);
    }
  }

  /** `CardEntryForm`'s `onCardSuccess` — the new card just confirmed with the provider. */
  async function handleCardSaved(providerToken: string) {
    // Taken before the first await, so a provider that confirms twice still
    // produces one saved card and one booking.
    if (submitLatch.current) return;
    submitLatch.current = true;
    try {
      setIsProcessing(true);
      const databaseId = await saveCard(providerToken);
      const savedId = databaseId.toString();
      setShowNewPaymentForm(false);
      setSelectedPaymentMethodId(savedId);
      await submitBooking(savedId);
    } catch (err) {
      reportPaymentClientError('save_card', err, {
        kind: 'client_flow',
        surface: 'web_lesson',
        extra: { provider: paymentProvider },
      });
      setFailure(guardFailure(err));
      submitLatch.current = false;
    } finally {
      setIsProcessing(false);
    }
  }

  function handleRecover(recovery: LessonBookingRecovery) {
    setFailure(null);
    switch (recovery) {
      case 'pick-instructor':
        goToStep(0);
        break;
      case 'pick-bay':
        goToStep(1);
        break;
      case 'pick-time':
        goToStep(1);
        break;
      case 'add-payment':
        setShowNewPaymentForm(true);
        setSelectedPaymentMethodId(null);
        break;
      case 'requote':
        void quoteQuery.refetch();
        break;
      case 'retry':
      case 'none':
      default:
        break;
    }
  }

  // The button is disabled in these states already; this re-checks at the
  // moment of the click, BEFORE a `submit-card` lets the native submit reach
  // the card form — after that, the single-use SetupIntent is spent.
  const handleCtaClick = (event: MouseEvent<HTMLButtonElement>) => {
    if (cta.disabled || submitLatch.current) {
      event.preventDefault();
      return;
    }
    if (cta.action === 'confirm') {
      event.preventDefault();
      submitLatch.current = true;
      void handleConfirm();
      return;
    }
    // 'submit-card': the native `form=` submit goes ahead into CardEntryForm,
    // whose `onCardSuccess` chains into `handleCardSaved` above. No latch
    // here — see the note on `submitLatch`.
  };

  if (!selectionComplete || date === null || startTime === null || endTime === null) {
    // The effect above already redirects — render nothing while it does.
    return null;
  }

  const dateLabel = format(parseISO(date), 'EEEE, d MMMM, yyyy');
  const dateLabelCompact = format(parseISO(date), 'EEE, d MMM, yyyy');
  const timeRangeLabel = formatTimeRange(startTime, endTime);
  const durationLabel = formatLessonHours(durationMinutes ?? 0);
  const resolvedBayName = resolveLessonBayName(club?.bays, bayId);

  // The coverage card, from the quote's own answer about the package.
  const coverageCard = (() => {
    if (!coverage || coverage.kind === 'none') return null;
    const packName = coveragePack?.lesson?.name ?? LESSON_PACK_FALLBACK_NAME;
    const balanceLabel = coverage.unlimited
      ? 'Unlimited'
      : coveragePack
        ? `${formatLessonHours(coveragePack.remainingMinutes)} remaining`
        : '';
    const copy =
      coverage.kind === 'partial'
        ? lessonPartialCoverageSentence(coverage.coveredMinutes)
        : coverage.unlimited
          ? lessonCoverageSentence('full')
          : `${lessonCoverageSentence('full')} ${lessonHoursUsedSentence(coverage.coveredMinutes)}`;
    return <LessonCoverageCard packName={packName} balanceLabel={balanceLabel} copy={copy} />;
  })();

  // Price Summary — only when something is owed, and every figure is the
  // server quote's. A covered lesson has no price to summarise. Keyed on what is OWED,
  // not on whether a card is needed: a lesson credit pays still shows its price.
  const money = (value: number) => `$${value.toFixed(2)}`;
  const priceSummary: ReactNode =
    owed && totalCost ? (
      <TicketCard borderColor="var(--theme-100)" data-testid="lesson-price-summary">
        <div className="px-7 pt-7">
          <h2 className="text-sm font-normal text-butter-gray-600">Price Summary</h2>
          <dl className="mt-6 flex flex-col gap-4">
            {quote?.fees.map((fee) => (
              <LessonTicketRow key={fee.label} label={fee.label} value={money(fee.baseAmount)} />
            ))}
            {totalCost.taxAmount > 0 && (
              <LessonTicketRow
                label={`Tax (${formatFeeRate(totalCost.taxRatePercent)})`}
                value={money(totalCost.taxAmount)}
                data-testid="lesson-price-summary-tax"
              />
            )}
            {(totalCost.processingFee ?? 0) > 0 && (
              <LessonTicketRow
                label={`Processing Fee${totalCost.processingFeeRate != null ? ` (${formatFeeRate(totalCost.processingFeeRate)})` : ''}`}
                value={money(totalCost.processingFee ?? 0)}
                data-testid="lesson-price-summary-processing-fee"
              />
            )}
          </dl>
        </div>
        <TicketDivider punch className="my-8" />
        <dl className="px-7 pb-7">
          <LessonTicketRow label="Total" value={money(totalCost.total)} emphasis data-testid="lesson-price-summary-total" />
          {/* Account credit — a tender under the total (credits D9) */}
          {credit && (
            <>
              <LessonTicketRow
                label="Account credit"
                value={`-${money(credit.appliedCents / 100)}`}
                data-testid="lesson-price-summary-credit"
              />
              {/* The card fee on the credit's share, which the card does not pay */}
              {credit.feeAdjustmentCents !== undefined && credit.feeAdjustmentCents < 0 && (
                <LessonTicketRow
                  label="Processing Fee Adjustment"
                  value={`-${money(-credit.feeAdjustmentCents / 100)}`}
                  data-testid="lesson-price-summary-fee-adjustment"
                />
              )}
              <LessonTicketRow
                label="Due today"
                value={money(credit.dueNowCents / 100)}
                emphasis
                data-testid="lesson-price-summary-due-today"
              />
            </>
          )}
        </dl>
      </TicketCard>
    ) : null;

  return (
    <div data-testid="lesson-payment-step">
      <div className="grid grid-cols-1 lg:grid-cols-12 lg:items-start gap-8">
        {/* LEFT */}
        <div className="space-y-4 lg:col-span-5">
          <button
            type="button"
            onClick={() => goToStep(1)}
            disabled={busy}
            className="mb-4 inline-flex items-center gap-2 text-lg font-semibold text-fg-quaternary text-interactive:text-fg-tertiary disabled:cursor-not-allowed disabled:opacity-50"
            data-testid="lesson-back-to-time-btn"
          >
            <ArrowIcon className="h-4 w-4 rotate-180" />
            Back to Lesson Time
          </button>

          <LessonDetailsCard
            instructorName={displayInstructorName}
            instructorImage={displayInstructorImage}
            instructorUnresolved={instructorUnresolved}
            bayName={resolvedBayName}
            dateLabel={dateLabel}
            dateLabelCompact={dateLabelCompact}
            timeRangeLabel={timeRangeLabel}
            durationLabel={durationLabel}
          />

          {overLimit && (
            <LessonFailureNotice
              failure={LESSON_DURATION_LIMIT_FAILURE}
              onRecover={handleRecover}
              autoFocus={false}
              testId="lesson-duration-limit-notice"
            />
          )}

          {quoteRefused && (
            <QuoteRefusedPanel
              errors={quote?.errors.length ? quote.errors : ["This lesson can't be booked. Please pick another time."]}
              testId="lesson-quote-refused"
            />
          )}
          {quoteFailed && <RetryBanner message="We couldn't get a price for this lesson." onRetry={() => quoteQuery.refetch()} testId="lesson-quote-failed" />}

          {coverageCard}
          {priceSummary}
        </div>

        {/* RIGHT */}
        <div className="space-y-8 lg:col-span-5 lg:col-start-8">
          {creditCovered && (
            <div
              className="flex items-start gap-3 rounded-2xl border border-theme-600 bg-theme-50 px-4 py-3"
              data-testid="lesson-no-card-needed"
            >
              <CheckmarkIcon className="mt-0.5 size-5 shrink-0 text-green-600" aria-hidden="true" />
              <p className="text-base font-medium text-fg-primary">Paid in full with account credit — no card needed</p>
            </div>
          )}
          {needsCard && (
            <CheckoutSection title="Payment Method" headingClassName="text-2xl font-semibold text-fg-secondary" headingTestId="lesson-payment-method-title">
              {isAuthenticated && isLoadingPaymentMethods ? (
                <p className="text-sm text-gray-500">Loading payment methods…</p>
              ) : !showingNewPaymentForm ? (
                <div className="pt-2">
                <PaymentMethodSelector
                  variant="tile"
                  heading={null}
                  unselectableMethodTypes={UNSELECTABLE_METHOD_TYPES}
                  unselectableReason="Bank accounts can't be used for payment yet"
                  paymentMethods={paymentMethods}
                  selectedPaymentMethodId={effectiveSelectedPaymentMethodId}
                  onSelect={(id) => setSelectedPaymentMethodId(id)}
                  onAddNew={() => {
                    setShowNewPaymentForm(true);
                    setSelectedPaymentMethodId(null);
                  }}
                  isProcessing={busy}
                />
                </div>
              ) : (
                <>
                  <CardEntryForm
                    paymentProvider={paymentProvider}
                    stripeSetupIntent={cardSaveWiring.stripeSetupIntent}
                    stripeSetupIntentLoading={cardSaveWiring.stripeSetupIntentLoading}
                    stripeSetupIntentError={cardSaveWiring.stripeSetupIntentError}
                    retryStripeSetupIntent={cardSaveWiring.retryStripeSetupIntent}
                    paymentSettings={effectivePaymentSettings}
                    onCardSuccess={handleCardSaved}
                    errorLabel="LessonPaymentStep"
                    defaultEmail={authUser?.email}
                    errorContext={{ userId, clubId: club?.id }}
                    formId={LESSON_CARD_FORM_ID}
                    hideSubmitButton
                    onReadyChange={setCardReady}
                    onSubmittingChange={setCardSubmitting}
                  />
                  {paymentProvider === 'stripe' && (
                    <p className="text-center text-xs text-butter-gray-500">
                      Powered by <span className="font-semibold text-butter-gray-700">Stripe</span>
                    </p>
                  )}
                  {paymentMethods.length > 0 && showNewPaymentForm && (
                    <button
                      type="button"
                      onClick={() => setShowNewPaymentForm(false)}
                      disabled={busy}
                      className="mt-4 text-sm text-theme-600 text-interactive:text-theme-700 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      ← Back to saved payment methods
                    </button>
                  )}
                </>
              )}
            </CheckoutSection>
          )}

          <WaiverSection clubId={club?.id} disabled={busy} divider />

          {/* `key` forces a fresh mount (and re-focus) on every new failure,
              even when the member retries directly without going through a
              recovery button first. */}
          {failure && (
            <LessonFailureNotice key={failure.message} failure={failure} onRecover={handleRecover} />
          )}

          <div className="space-y-4 pt-4">
            <button
              type={cta.action === 'submit-card' ? 'submit' : 'button'}
              form={cta.action === 'submit-card' ? LESSON_CARD_FORM_ID : undefined}
              onClick={handleCtaClick}
              disabled={cta.disabled}
              aria-busy={busy}
              className={CTA_CLASSES}
              data-testid={cta.testId}
            >
              {cta.label}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
